import { getNames, registerLocale } from 'i18n-iso-countries';
import deCountryLocale from 'i18n-iso-countries/langs/de.json';
import enCountryLocale from 'i18n-iso-countries/langs/en.json';
import esCountryLocale from 'i18n-iso-countries/langs/es.json';
import { useContext } from 'react';

import deConfig from '../locales/de/config.json';
import deTranslations from '../locales/de/translation.json';
import enConfig from '../locales/en/config.json';
import enTranslations from '../locales/en/translation.json';
import esConfig from '../locales/es/config.json';
import esTranslations from '../locales/es/translation.json';
import { hmrSafeContext } from '../utils/hmrSafeContext';

// Initialize the countries library with locale data
registerLocale(deCountryLocale);
registerLocale(enCountryLocale);
registerLocale(esCountryLocale);

export interface LocaleConfig {
  defaultLang?: boolean;
  path: string;
  country: string;
  identifier: string;
  dateFormat: string;
  currency: string;
}

export interface Translation {
  t: (
    key: string,
    options?: {
      pluralCount?: number;
      placeholders?: { [key: string]: string | number };
    },
  ) => string;
  config: LocaleConfig;
  locale: string;
  allLocales: { locale: string; country: string }[];
  country: string;
  localizedCountryCodes: Record<string, string>;
  allLocaleConfigs: { [key: string]: LocaleConfig };
  urlPath: string;
}

export const translations = {
  en: enTranslations,
  de: deTranslations,
  es: esTranslations,
};

export const configs = {
  en: enConfig,
  de: deConfig,
  es: esConfig,
};

export const I18nContext = hmrSafeContext<Translation | null>('__turfmaster_i18n_ctx__', null);

export const getLocalizedCountryCodes = (locale: string): Record<string, string> => {
  return getNames(locale, { select: 'official' });
};

export const createFallbackContext = (urlPath = '/'): Translation => ({
  t: (key: string) => key,
  config: configs.de,
  locale: 'de',
  allLocales: [
    { locale: 'de', country: 'de' },
    { locale: 'en', country: 'en' },
    { locale: 'es', country: 'es' },
  ],
  country: 'de',
  localizedCountryCodes: getLocalizedCountryCodes('de'),
  allLocaleConfigs: configs,
  urlPath,
});

export const useTranslation = (): Translation => {
  const context = useContext(I18nContext);
  if (!context) {
    return createFallbackContext();
  }
  return context;
};
